import type { ReactNode } from 'react'
import type { BookLevel, OrderBook, TradePrint } from '@/lib/market/live'
import type { LiveSpec } from '@/lib/market/types'

import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { useLive } from '@/components/market/live-provider'
import { buildOrderBook, buildRecentTrades, quoteAt } from '@/lib/market/live'
import { formatNumber, formatPercent, formatPrice } from '@/lib/market/format'
import DepthChart from '@/components/charts/depth-chart'

interface OrderBookPanelProps {
    ticker: string
    day: number
    live: LiveSpec
    averageVolume: number
    initialBook: OrderBook
    initialTrades: TradePrint[]
}

const LEVELS = 8

const LadderRow = ({ level, side, max }: { level: BookLevel | undefined, side: 'bid' | 'ask', max: number }): ReactNode => {
    if (!level) return <div className='h-7' />

    const width = `${Math.max(2, (level.size / max) * 100)}%`

    return (
        <div className={cn('relative flex h-7 items-center gap-2 px-2 text-xs tabular-nums', side === 'bid' ? 'flex-row' : 'flex-row-reverse')}>
            <span
                aria-hidden='true'
                className={cn('absolute inset-y-0.5 rounded-sm', side === 'bid' ? 'right-0 bg-up/12' : 'left-0 bg-down/12')}
                style={{ width }}
            />
            <span className='relative w-8 text-muted-foreground'>{level.orders}</span>
            <span className='relative flex-1 text-foreground/85'>{formatNumber(level.size)}</span>
            <span className={cn('relative w-16 font-medium', side === 'bid' ? 'text-right text-up' : 'text-left text-down')}>{formatPrice(level.price)}</span>
        </div>
    )
}

const OrderBookPanel = ({ ticker, day, live, averageVolume, initialBook, initialTrades }: OrderBookPanelProps): ReactNode => {
    const { phase, progress, tick, stale } = useLive()
    const [book, setBook] = useState<OrderBook>(initialBook)
    const [trades, setTrades] = useState<TradePrint[]>(initialTrades)
    const open = phase === 'open' && !stale

    useEffect(() => {
        if (!open) return

        const quote = quoteAt(live, progress)
        setBook(buildOrderBook(ticker, quote, averageVolume, Math.floor(Date.now() / 15000)))
        setTrades(buildRecentTrades(ticker, day, live, progress))
    }, [open, tick, progress, ticker, day, live, averageVolume])

    const bids = book.bids.slice(0, LEVELS)
    const asks = book.asks.slice(0, LEVELS)
    const max = Math.max(1, ...bids.map(level => level.size), ...asks.map(level => level.size))
    const bidTotal = bids.reduce((acc, level) => acc + level.size, 0)
    const askTotal = asks.reduce((acc, level) => acc + level.size, 0)
    const spreadPct = book.mid > 0 ? (book.spread / book.mid) * 100 : 0

    return (
        <div className='space-y-5'>
            <dl className='grid grid-cols-3 gap-2 text-xs'>
                <div className='rounded-lg border border-border bg-background/50 px-2.5 py-2'>
                    <dt className='text-muted-foreground'>Spread</dt>
                    <dd className='mt-0.5 font-semibold tabular-nums text-foreground'>{formatPrice(book.spread)} <span className='font-normal text-muted-foreground'>({formatPercent(spreadPct, 2, false)})</span></dd>
                </div>
                <div className='rounded-lg border border-border bg-background/50 px-2.5 py-2'>
                    <dt className='text-muted-foreground'>Mid price</dt>
                    <dd className='mt-0.5 font-semibold tabular-nums text-foreground'>{formatNumber(book.mid, 3)}</dd>
                </div>
                <div className='rounded-lg border border-border bg-background/50 px-2.5 py-2'>
                    <dt className='text-muted-foreground'>Imbalance</dt>
                    <dd className={cn('mt-0.5 font-semibold tabular-nums', book.imbalance >= 0 ? 'text-up' : 'text-down')}>
                        {formatPercent(book.imbalance * 100, 0)} <span className='font-normal text-muted-foreground'>{book.imbalance >= 0 ? 'bids' : 'offers'}</span>
                    </dd>
                </div>
            </dl>

            <div>
                <div className='mb-1 grid grid-cols-2 gap-2 text-[11px] uppercase tracking-[0.06em] text-muted-foreground'>
                    <div className='flex justify-between px-2'><span>Orders</span><span>Size</span><span>Bid</span></div>
                    <div className='flex justify-between px-2'><span>Offer</span><span>Size</span><span>Orders</span></div>
                </div>

                <p className='sr-only'>
                    {`${ticker} order book. Best bid ${bids[0] ? formatPrice(bids[0].price) : 'none'}, best offer ${asks[0] ? formatPrice(asks[0].price) : 'none'}.`}
                </p>

                <div className='grid grid-cols-2 gap-2' aria-hidden='true'>
                    <div>
                        {Array.from({ length: LEVELS }, (_, index) => <LadderRow key={`bid-${index}`} level={bids[index]} side='bid' max={max} />)}
                    </div>
                    <div>
                        {Array.from({ length: LEVELS }, (_, index) => <LadderRow key={`ask-${index}`} level={asks[index]} side='ask' max={max} />)}
                    </div>
                </div>

                <div className='mt-2 flex justify-between px-2 text-[11px] tabular-nums text-muted-foreground'>
                    <span>{formatNumber(bidTotal)} shares bid</span>
                    <span>{formatNumber(askTotal)} shares offered</span>
                </div>
            </div>

            <DepthChart bids={book.bids} asks={book.asks} />

            <div>
                <h3 className='mb-2 text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground'>Time and sales</h3>

                {trades.length === 0 ? (
                    <p className='rounded-lg border border-dashed border-border px-3 py-6 text-center text-xs text-muted-foreground'>No trades yet this session.</p>
                ) : (
                    <table className='w-full text-xs tabular-nums'>
                        <caption className='sr-only'>Most recent trades in {ticker}</caption>
                        <thead>
                            <tr className='text-left text-[11px] text-muted-foreground'>
                                <th scope='col' className='pb-1 font-medium'>Time</th>
                                <th scope='col' className='pb-1 text-right font-medium'>Price</th>
                                <th scope='col' className='pb-1 text-right font-medium'>Shares</th>
                            </tr>
                        </thead>
                        <tbody>
                            {trades.map(trade => (
                                <tr key={trade.id} className='border-t border-border/50'>
                                    <td className='py-1 text-muted-foreground'>{trade.time}</td>
                                    <td className={cn('py-1 text-right font-medium', trade.side === 'buy' ? 'text-up' : 'text-down')}>{formatPrice(trade.price)}</td>
                                    <td className='py-1 text-right text-foreground/85'>{formatNumber(trade.size)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    )
}

export default OrderBookPanel
